import { useContext, useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { getFaqArticle, getFaqCategories } from "app/reducers/partnerActionCreators";
import { matchPath, Route, Routes, useLocation } from "react-router-dom";
import FaqArticle from "app/pages/Faq/FaqArticle/FaqArticle";
import FaqCategory from "app/pages/Faq/FaqCategory/FaqCategory";
import FaqSubCategory from "app/pages/Faq/FaqSubCategory/FaqSubCategory";
import FaqHomeContainer from "app/pages/Faq/FaqHome/FaqHomeContainer";
import FaqHeader from "app/pages/Faq/FaqHeader/FaqHeader";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import AppGlobalsContext from "app/AppGlobalsContext";
import FaqFooter from "app/pages/Faq/FaqFooter/FaqFooter";
import Loader from "app/pages/.shared/Loader";
import "./Faq.scss";
import { FormattedMessage } from "react-intl";
import FaqSearch from "app/pages/Faq/FaqSearch/FaqSearch";
import classNames from "classnames";
import { getFallbackRedirectUrl, getShowPfsMessage } from "app/reducers/partnerSelector";
import FaqLayout from "./FaqLayout";

const FaqContainer = ({
	getFaqCategories,
	getFaqArticle,
	fallbackRedirectUrl,
	showPfsMessage,
	algoliaFaqIndexName,
	hideContactUsSection = false,
}) => {
	const [isLoading, setLoading] = useState(true);
	const [faqCategories, setCategories] = useState([]);
	const [faqSpotlight, setFaqSpotlight] = useState({});
	const [hideContactByPhone, setHideContactByPhone] = useState(false);
	const [hideContactByMail, setHideContactByMail] = useState(false);
	const [article, setArticle] = useState({});

	const { resolution, brand } = useContext(AppGlobalsContext);

	useEffect(() => {
		setLoading(true);

		getFaqCategories().then(response => {
			setLoading(false);
			const data = response.data || {};
			setCategories(data.categories);
			setFaqSpotlight(data.spotlight);
			const brandsOnly = data?.hiddenContactBrands?.map(brand => brand?.code) || [];
			const shouldApplyHiddenContact = brandsOnly.includes(brand);
			if (shouldApplyHiddenContact) {
				setHideContactByPhone(data.hideContactByPhone);
				setHideContactByMail(data.hideContactByMail);
			}
		});
	}, []);

	const location = useLocation();

	const match = matchPath(
		{
			path: "/faq/category/:categoryId/subcategory/:subCategoryId/article/:articleId",
		},
		location.pathname
	);

	let articleId;
	let categoryId;
	let subCategoryId;

	if (match && match.params.articleId && match.params.categoryId && match.params.subCategoryId) {
		articleId = match.params.articleId;
		categoryId = match.params.categoryId;
		subCategoryId = match.params.subCategoryId;
	}

	useEffect(() => {
		if (articleId) {
			setLoading(true);
			const articleFileName = `${categoryId}-${subCategoryId}-${articleId}`;
			getFaqArticle(articleFileName).then(response => {
				setLoading(false);
				const article = response.data;
				setArticle(article);
			});
		} else {
			setArticle({});
		}
	}, [categoryId, subCategoryId, articleId]);

	const isFaqHome = location.pathname === "/faq";

	const isDesktop = resolution === RESOLUTION.LARGE || resolution === RESOLUTION.XLARGE;

	const searchClassname = classNames({
		"faq__search faq__search--hero": true,
		"faq__search--small": !isDesktop,
	});

	return (
		<div className="faq">
			<FaqHeader
				faqCategories={faqCategories}
				showPfsMessage={showPfsMessage}
				algoliaFaqIndexName={algoliaFaqIndexName}
			/>

			{(isFaqHome || !isDesktop) && (
				<div className={searchClassname}>
					<div className="faq-header__title">
						<FormattedMessage id="faq.search.title" />
					</div>
					<FaqSearch algoliaFaqIndexName={algoliaFaqIndexName} />
				</div>
			)}

			{isLoading && (
				<div className="faq__loader">
					<Loader />
				</div>
			)}

			<Routes>
				<Route
					path={"/category/:categoryId/subcategory/:subCategoryId/article/:articleId"}
					element={
						<FaqLayout faqCategories={faqCategories} article={article}>
							<FaqArticle title={article.title} content={article.content} />
						</FaqLayout>
					}
				/>
				<Route
					path={"/category/:categoryId/subcategory/:subCategoryId"}
					element={
						<FaqLayout faqCategories={faqCategories} article={article}>
							<FaqSubCategory faqCategories={faqCategories} />
						</FaqLayout>
					}
				/>
				<Route
					path={"/category/:categoryId"}
					element={
						<FaqLayout faqCategories={faqCategories} article={article}>
							<FaqCategory faqCategories={faqCategories} />
						</FaqLayout>
					}
				/>
				<Route
					path={"/"}
					element={
						<>
							{!isLoading && (
								<FaqHomeContainer
									faqCategories={faqCategories}
									faqSpotlight={faqSpotlight}
								/>
							)}
						</>
					}
				/>
			</Routes>
			<FaqFooter
				hideContactByPhone={hideContactByPhone}
				hideContactByMail={hideContactByMail}
				fallbackRedirectUrl={fallbackRedirectUrl}
				hideContactUsSection={hideContactUsSection}
			/>
		</div>
	);
};

FaqContainer.propTypes = {
	getFaqCategories: PropTypes.func,
	getFaqArticle: PropTypes.func,
	fallbackRedirectUrl: PropTypes.string,
	showPfsMessage: PropTypes.bool,
	algoliaFaqIndexName: PropTypes.string,
	hideContactUsSection: PropTypes.bool,
};

const mapDispatchToProps = dispatch => {
	return bindActionCreators({ getFaqCategories, getFaqArticle }, dispatch);
};

const mapStateToProps = state => {
	return {
		fallbackRedirectUrl: getFallbackRedirectUrl(state),
		showPfsMessage: getShowPfsMessage(state),
		algoliaFaqIndexName: state.envVars.ALGOLIA_FAQ_INDEX_NAME,
		hideContactUsSection: state.partner.hideContactUsSection,
	};
};

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(FaqContainer);
